import { useCallback, useEffect, useRef, useState } from "react";

import { isValidAction } from "@fusiqo/vozqi-shared";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type RenderOptions = {
  sitekey: string;
  action: string;
  theme: "light" | "dark" | "auto";
  language: string;
  callback: (token: string) => void;
  "expired-callback": () => void;
  // Cloudflare passes an error code here — "300030", "110200" and so on. It is
  // the only description of what went wrong that ever exists: the widget draws
  // a generic box, and nothing reaches the server, because a challenge that
  // errors produces no token to send.
  "error-callback": (code?: string) => void;
};

type TurnstileApi = {
  render(container: HTMLElement, options: RenderOptions): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptLoading: Promise<TurnstileApi> | undefined;

/**
 * Loads api.js once per document, however many widgets ask for it.
 *
 * `render=explicit` matters: the default auto-render scans the DOM for
 * `.cf-turnstile` at load and never sees anything React mounts afterwards, and
 * it hands back no widget id — which is the one thing needed to reset a widget
 * after a failed submit.
 */
function loadTurnstile(): Promise<TurnstileApi> {
  if (scriptLoading !== undefined) return scriptLoading;

  scriptLoading = new Promise<TurnstileApi>((resolve, reject) => {
    if (window.turnstile !== undefined) {
      resolve(window.turnstile);
      return;
    }

    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => {
      if (window.turnstile === undefined) {
        reject(new Error("turnstile: api.js loaded without defining window.turnstile"));
        return;
      }
      resolve(window.turnstile);
    });
    // A blocked script is the common case, not the exotic one: a content
    // blocker, a corporate proxy, an offline laptop. The promise rejects so the
    // hook can report it rather than leaving a box that never appears and a
    // button that never enables.
    script.addEventListener("error", () => {
      scriptLoading = undefined;
      reject(new Error("turnstile: api.js could not be loaded"));
    });
    document.head.append(script);
  });

  return scriptLoading;
}

export type TurnstileState = {
  /** Mount this on the element the widget should draw into. */
  ref: (node: HTMLDivElement | null) => void;
  /** The current token, or null while unsolved, expired or already spent. */
  token: string | null;
  /** True once the challenge is solved and a token is in hand. */
  ready: boolean;
  /** Set when api.js could not load at all. */
  failed: boolean;
  /**
   * Cloudflare's code for a challenge that ERRORED, as opposed to one that
   * could not be loaded, or null.
   *
   * Worth surfacing rather than folding into `failed` because the two have
   * different causes and different fixes, and this is the only evidence either
   * produces. A challenge that fails inside a WebView leaves no server log —
   * there is no token, so no request — and no console anybody can reach.
   */
  errorCode: string | null;
  /**
   * Throws the current token away and asks for a fresh challenge. Call it after
   * every submit attempt: a token is redeemed exactly once at siteverify, so
   * the one that just went out is spent whether the request succeeded or not.
   */
  reset: () => void;
};

/**
 * Draws a Turnstile widget and hands back its token.
 *
 * A hook rather than a component because the token has to reach the submit
 * handler, and a component would mean lifting it through props on every form
 * that uses one. The DOM node stays outside React's control — api.js renders
 * into it — which is why the ref is a callback: it fires on mount with the node
 * and on unmount with null, and both are needed to keep exactly one widget
 * alive per form.
 */
export function useTurnstile(options: {
  sitekey: string | undefined;
  action: string;
  theme?: "light" | "dark" | "auto";
  language?: string;
}): TurnstileState {
  const { sitekey, action } = options;
  const theme = options.theme ?? "auto";
  const language = options.language ?? "es";

  const [token, setToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const widgetId = useRef<string | null>(null);
  const api = useRef<TurnstileApi | null>(null);

  if (!isValidAction(action)) {
    throw new Error(`turnstile: "${action}" is not a valid action name`);
  }

  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      if (node === null) {
        const id = widgetId.current;
        if (id !== null && api.current !== null) api.current.remove(id);
        widgetId.current = null;
        return;
      }

      // No sitekey configured is not an error: it is the app running with the
      // capability switched off, and the form must still work.
      if (sitekey === undefined || sitekey === "") return;

      let cancelled = false;
      void loadTurnstile()
        .then((turnstile) => {
          if (cancelled || widgetId.current !== null) return;
          api.current = turnstile;
          widgetId.current = turnstile.render(node, {
            sitekey,
            action,
            theme,
            language,
            callback: (value) => {
              setToken(value);
              setErrorCode(null);
            },
            // An expired token is worse than no token: it is refused at
            // siteverify and the visitor is told they are a robot for having
            // filled the form slowly.
            "expired-callback": () => setToken(null),
            "error-callback": (code) => {
              setToken(null);
              setErrorCode(code ?? "unknown");
            },
          });
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });

      return () => {
        cancelled = true;
      };
    },
    [sitekey, action, theme, language],
  );

  const reset = useCallback(() => {
    setToken(null);
    setErrorCode(null);
    const id = widgetId.current;
    if (id !== null && api.current !== null) api.current.reset(id);
  }, []);

  useEffect(() => {
    return () => {
      const id = widgetId.current;
      if (id !== null && api.current !== null) api.current.remove(id);
      widgetId.current = null;
    };
  }, []);

  return { ref, token, ready: token !== null, failed, errorCode, reset };
}
